import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner, Table, Modal } from 'react-bootstrap';
import apiService from '../services/api';
import VersionInfo from './VersionInfo';

const emptyProductForm = {
    name: '',
    description: '',
    model_code: '',
    cost_price: 0,
    selling_price: 0,
    sales_rep_commission: 0,
    unit_sizes: '',
    is_active: true,
    ordinal: 0
};

const ProductDataDefaults = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [settingData, setSettingData] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        brand: '',
        currency_code: '',
        is_active: true,
        ordinal: 0,
        list: []
    });

    // Product editor modal state
    const [showProductModal, setShowProductModal] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [productForm, setProductForm] = useState(emptyProductForm);

    useEffect(() => {
        loadProductData();
    }, []);

    const loadProductData = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiService.getOrganizationDefaultSettingByCode('product_data');
            if (response.success && response.data) {
                setSettingData(response.data);
                const settings = response.data.value || {};
                setFormData({
                    brand: settings.brand || '',
                    currency_code: settings.currency_code || '',
                    is_active: settings.is_active !== undefined ? Boolean(settings.is_active) : true,
                    ordinal: Number(settings.ordinal) || 0,
                    list: Array.isArray(settings.list) ? settings.list.map(p => ({
                        name: p.name || '',
                        description: p.description || '',
                        model_code: p.model_code || '',
                        cost_price: Number(p.cost_price) || 0,
                        selling_price: Number(p.selling_price) || 0,
                        sales_rep_commission: Number(p.sales_rep_commission) || 0,
                        unit_sizes: Array.isArray(p.unit_sizes) ? p.unit_sizes.map(Number) : [],
                        is_active: p.is_active !== undefined ? Boolean(p.is_active) : true,
                        ordinal: Number(p.ordinal) || 0
                    })) : []
                });
            } else if (response.error && !String(response.error).toLowerCase().includes('not found')) {
                setError(response.error);
            }
        } catch (err) {
            console.error('Error loading product data:', err);
            setError('Failed to load product data');
        } finally {
            setLoading(false);
        }
    };

    const handleHeaderChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const openAddProduct = () => {
        setEditingIndex(null);
        setProductForm({ ...emptyProductForm });
        setShowProductModal(true);
    };

    const openEditProduct = (index) => {
        const p = formData.list[index];
        setEditingIndex(index);
        setProductForm({
            name: p.name,
            description: p.description,
            model_code: p.model_code,
            cost_price: p.cost_price,
            selling_price: p.selling_price,
            sales_rep_commission: p.sales_rep_commission,
            unit_sizes: (p.unit_sizes || []).join(', '),
            is_active: p.is_active !== undefined ? Boolean(p.is_active) : true,
            ordinal: Number(p.ordinal) || 0
        });
        setShowProductModal(true);
    };

    const handleProductFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProductForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const parseUnitSizes = (raw) => {
        if (!raw) return [];
        return String(raw)
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0)
            .map(s => Number(s))
            .filter(n => !Number.isNaN(n));
    };

    const handleSaveProduct = (e) => {
        e.preventDefault();
        const product = {
            name: productForm.name.trim(),
            description: productForm.description.trim(),
            model_code: productForm.model_code.trim(),
            cost_price: Number(productForm.cost_price) || 0,
            selling_price: Number(productForm.selling_price) || 0,
            sales_rep_commission: Number(productForm.sales_rep_commission) || 0,
            unit_sizes: parseUnitSizes(productForm.unit_sizes),
            is_active: Boolean(productForm.is_active),
            ordinal: Number(productForm.ordinal) || 0
        };

        if (!product.name) {
            return;
        }

        setFormData(prev => {
            const list = [...prev.list];
            if (editingIndex === null) {
                list.push(product);
            } else {
                list[editingIndex] = product;
            }
            return { ...prev, list };
        });
        setShowProductModal(false);
    };

    const handleDeleteProduct = (index) => {
        setFormData(prev => ({
            ...prev,
            list: prev.list.filter((_, i) => i !== index)
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const updateData = {
                name: 'Product Data',
                code: 'product_data',
                value: formData
            };

            let response;
            if (settingData && settingData.id) {
                response = await apiService.updateOrganizationDefaultSetting(settingData.id, updateData);
            } else {
                response = await apiService.createOrganizationDefaultSetting(updateData);
            }

            if (response.success) {
                setSuccess('Product data saved successfully!');
                setSettingData(response.data);
            } else {
                setError(response.error || 'Failed to save product data');
            }
        } catch (err) {
            console.error('Error saving product data:', err);
            setError('Failed to save product data');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <div className="mt-2">Loading product data...</div>
            </div>
        );
    }

    return (
        <div>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>
                            <i className="bi bi-box-seam me-2"></i>
                            Product Data Defaults
                        </h2>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" dismissible onClose={() => setError('')}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {success && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                            <i className="bi bi-check-circle me-2"></i>
                            {success}
                        </Alert>
                    </Col>
                </Row>
            )}

            <Form onSubmit={handleSave}>
                <Row>
                    <Col lg={8}>
                        <Card className="mb-3">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <i className="bi bi-sliders me-2"></i>
                                    Brand & Currency
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={8}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Brand</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="brand"
                                                value={formData.brand}
                                                onChange={handleHeaderChange}
                                                placeholder="e.g. Safaricom"
                                            />
                                            <Form.Text className="text-muted">
                                                Default brand applied to product entries.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Currency Code</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="currency_code"
                                                value={formData.currency_code}
                                                onChange={handleHeaderChange}
                                                placeholder="e.g. KES"
                                                maxLength={3}
                                            />
                                            <Form.Text className="text-muted">
                                                ISO 4217 currency code.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Ordinal</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                step="1"
                                                name="ordinal"
                                                value={formData.ordinal}
                                                onChange={handleHeaderChange}
                                            />
                                            <Form.Text className="text-muted">
                                                Display order for this brand.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4} className="d-flex align-items-center">
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="switch"
                                                id="brand-is-active"
                                                name="is_active"
                                                label="Active"
                                                checked={Boolean(formData.is_active)}
                                                onChange={handleHeaderChange}
                                            />
                                            <Form.Text className="text-muted">
                                                Inactive brands are hidden from selection.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Card className="mb-3">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="bi bi-list-ul me-2"></i>
                                    Products
                                </h5>
                                <Button variant="primary" size="sm" type="button" onClick={openAddProduct}>
                                    <i className="bi bi-plus-lg me-1"></i>
                                    Add Product
                                </Button>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {formData.list.length === 0 ? (
                                    <div className="text-center text-muted p-4">
                                        No products configured. Click "Add Product" to create one.
                                    </div>
                                ) : (
                                    <Table responsive hover className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Model Code</th>
                                                <th className="text-end">Cost</th>
                                                <th className="text-end">Selling</th>
                                                <th className="text-end">Commission</th>
                                                <th>Unit Sizes</th>
                                                <th className="text-end">Ordinal</th>
                                                <th className="text-center">Active</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.list.map((p, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <div className="fw-semibold">{p.name}</div>
                                                        {p.description && (
                                                            <div className="small text-muted">{p.description}</div>
                                                        )}
                                                    </td>
                                                    <td><code>{p.model_code}</code></td>
                                                    <td className="text-end">{p.cost_price}</td>
                                                    <td className="text-end">{p.selling_price}</td>
                                                    <td className="text-end">{p.sales_rep_commission}</td>
                                                    <td>{(p.unit_sizes || []).join(', ')}</td>
                                                    <td className="text-end">{p.ordinal || 0}</td>
                                                    <td className="text-center">
                                                        {p.is_active ? (
                                                            <span className="badge bg-success">Active</span>
                                                        ) : (
                                                            <span className="badge bg-secondary">Inactive</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            type="button"
                                                            className="me-1"
                                                            onClick={() => openEditProduct(idx)}
                                                        >
                                                            <i className="bi bi-pencil"></i>
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            type="button"
                                                            onClick={() => handleDeleteProduct(idx)}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                            <Card.Footer>
                                <div className="d-flex justify-content-end">
                                    <Button variant="primary" type="submit" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-lg me-2"></i>
                                                Save Settings
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Card.Footer>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card className="bg-light mb-3">
                            <Card.Header>
                                <h6 className="mb-0">
                                    <i className="bi bi-info-circle me-2"></i>
                                    About Product Data
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                <p className="mb-2">
                                    <strong>Brand:</strong> The default brand applied to product listings.
                                </p>
                                <p className="mb-2">
                                    <strong>Currency Code:</strong> ISO currency code used for all prices.
                                </p>
                                <p className="mb-2">
                                    <strong>Products:</strong> Catalog of default products with pricing and unit sizes.
                                </p>
                                <p className="mb-0">
                                    <strong>Unit Sizes:</strong> Comma-separated quantities each product can be sold in (e.g. <code>100, 1000</code>).
                                </p>
                            </Card.Body>
                        </Card>

                        <VersionInfo />
                    </Col>
                </Row>
            </Form>

            <Modal show={showProductModal} onHide={() => setShowProductModal(false)} size="lg" backdrop="static">
                <Form onSubmit={handleSaveProduct}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="bi bi-box-seam me-2"></i>
                            {editingIndex === null ? 'Add Product' : 'Edit Product'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={productForm.name}
                                        onChange={handleProductFormChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Model Code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="model_code"
                                        value={productForm.model_code}
                                        onChange={handleProductFormChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="description"
                                value={productForm.description}
                                onChange={handleProductFormChange}
                            />
                        </Form.Group>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Cost Price</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        name="cost_price"
                                        value={productForm.cost_price}
                                        onChange={handleProductFormChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Selling Price</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        name="selling_price"
                                        value={productForm.selling_price}
                                        onChange={handleProductFormChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Sales Rep Commission</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        name="sales_rep_commission"
                                        value={productForm.sales_rep_commission}
                                        onChange={handleProductFormChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Unit Sizes</Form.Label>
                            <Form.Control
                                type="text"
                                name="unit_sizes"
                                value={productForm.unit_sizes}
                                onChange={handleProductFormChange}
                                placeholder="e.g. 100, 1000"
                            />
                            <Form.Text className="text-muted">
                                Comma-separated list of numeric unit sizes.
                            </Form.Text>
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ordinal</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="1"
                                        name="ordinal"
                                        value={productForm.ordinal}
                                        onChange={handleProductFormChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Display order for this product.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6} className="d-flex align-items-center">
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="product-is-active"
                                        name="is_active"
                                        label="Active"
                                        checked={Boolean(productForm.is_active)}
                                        onChange={handleProductFormChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Inactive products are hidden from selection.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" type="button" onClick={() => setShowProductModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            <i className="bi bi-check-lg me-2"></i>
                            {editingIndex === null ? 'Add Product' : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default ProductDataDefaults;
